import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { useShop } from '../context/ShopContext';
import Header from '../components/Header';

const API_URL = process.env.REACT_APP_API_URL;
const SHOP_NAME = process.env.REACT_APP_SHOP_NAME || 'Shop';

export default function TermsPage() {
    const { merchantId } = useShop();
    const [shopSettings, setShopSettings] = useState(null);
    const [contentMode, setContentMode] = useState('default');
    const [customContent, setCustomContent] = useState(null);
    const [customUrl, setCustomUrl] = useState(null);

    const fetchShopSettings = useCallback(async () => {
        if (!merchantId) return;
        try {
            const response = await axios.get(`${API_URL}/store/${merchantId}/settings`);
            setShopSettings(response.data);
            const mode = response.data.terms_content_mode || 'default';
            setContentMode(mode);
            if (mode === 'url' && response.data.custom_terms_url) {
                setCustomUrl(response.data.custom_terms_url);
            } else if (mode === 'text' && response.data.terms_content) {
                setCustomContent(response.data.terms_content);
            }
        } catch (error) {
            console.error('Failed to fetch shop settings:', error);
        }
    }, [merchantId]);

    useEffect(() => {
        if (merchantId) fetchShopSettings();
    }, [merchantId, fetchShopSettings]);

    const displayName = shopSettings?.shop_name || SHOP_NAME;

    // Wait for shop context (merchantId from subdomain or env)
    if (!merchantId) {
        return (
            <div className="min-h-screen bg-background flex flex-col">
                <Header />
                <main className="flex-1 flex items-center justify-center">
                    <p className="text-muted-foreground">Φόρτωση...</p>
                </main>
            </div>
        );
    }

    // If merchant has custom terms URL, show redirect option
    if (contentMode === 'url' && customUrl) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <Link 
                        to="/" 
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Πίσω στο Shop
                    </Link>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl shadow-card p-8 md:p-12 text-center"
                    >
                        <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                            <FileText className="w-8 h-8 text-primary" />
                        </div>
                        <h1 className="font-display text-2xl font-bold text-primary mb-4">
                            Όροι Χρήσης
                        </h1>
                        <p className="text-muted-foreground mb-6">
                            Το κατάστημα {displayName} έχει τους δικούς του Όρους Χρήσης.
                        </p>
                        <a 
                            href={customUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Δείτε τους Όρους Χρήσης
                        </a>
                    </motion.div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <Header />
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <Link 
                    to="/" 
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Πίσω στο Shop
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl shadow-card p-8 md:p-12"
                >
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                            <FileText className="w-7 h-7 text-primary" />
                        </div>
                        <div>
                            <h1 className="font-display text-3xl font-bold text-primary">
                                Όροι Χρήσης
                            </h1>
                            <p className="text-muted-foreground text-sm mt-1">
                                {displayName}
                            </p>
                        </div>
                    </div>

                    {contentMode === 'text' && customContent ? (
                        <div className="prose prose-gray max-w-none" dangerouslySetInnerHTML={{ __html: customContent }} />
                    ) : (
                        <div className="prose prose-gray max-w-none">
                            <p className="text-sm text-muted-foreground mb-6 border-l-4 border-primary/30 pl-4">
                                Οι παρακάτω όροι είναι πρότυπο για το κατάστημα. Ο έμπορος μπορεί να ορίσει δικό του κείμενο από τις Ρυθμίσεις στο Dashboard.
                            </p>
                            <h2>1. Γενικοί Όροι</h2>
                            <p>
                                Πραγματοποιώντας μια παραγγελία στο {displayName}, συμφωνείτε με τους παρόντες όρους και προϋποθέσεις.
                                Παρακαλούμε διαβάστε τους προσεκτικά πριν ολοκληρώσετε μια αγορά.
                            </p>

                            <h2>2. Παραγγελίες & Πληρωμές</h2>
                            <ul>
                                <li>Όλες οι τιμές είναι σε EUR και περιλαμβάνουν τους ισχύοντες φόρους</li>
                                <li>Η πληρωμή επεξεργάζεται με ασφάλεια μέσω των παρόχων πληρωμών μας</li>
                                <li>Οι παραγγελίες επιβεβαιώνονται μέσω email</li>
                                <li>Διατηρούμε το δικαίωμα να ακυρώσουμε παραγγελίες σε περίπτωση σφαλμάτων τιμολόγησης</li>
                            </ul>

                            <h2>3. Αποστολή & Παράδοση</h2>
                            <ul>
                                <li>Οι χρόνοι αποστολής είναι εκτιμήσεις και δεν είναι εγγυημένοι</li>
                                <li>Οι διευθύνσεις παράδοσης πρέπει να είναι πλήρεις και ακριβείς</li>
                                <li>Ο κίνδυνος απώλειας περνά σε εσάς κατά την παράδοση</li>
                            </ul>

                            <h2>4. Επιστροφές & Επιστροφές Χρημάτων</h2>
                            <ul>
                                <li>Έχετε 14 ημέρες για να επιστρέψετε αχρησιμοποίητα προϊόντα</li>
                                <li>Τα προϊόντα πρέπει να είναι στην αρχική τους κατάσταση και συσκευασία</li>
                                <li>Τα έξοδα επιστροφής ενδέχεται να ισχύουν</li>
                                <li>Οι επιστροφές χρημάτων επεξεργάζονται εντός 5-10 εργάσιμων ημερών</li>
                            </ul>

                            <h2>5. Πληροφορίες Προϊόντων</h2>
                            <p>
                                Προσπαθούμε να εμφανίζουμε ακριβείς πληροφορίες προϊόντων. Τα χρώματα μπορεί να διαφέρουν ελαφρώς
                                λόγω ρυθμίσεων οθόνης. Η διαθεσιμότητα των προϊόντων υπόκειται σε αλλαγές.
                            </p>

                            <h2>6. Πνευματική Ιδιοκτησία</h2>
                            <p>
                                Όλο το περιεχόμενο σε αυτή τη σελίδα ανήκει στο {displayName} ή στους δικαιοπαρόχους του
                                και προστατεύεται από τους νόμους περί πνευματικών δικαιωμάτων.
                            </p>

                            <h2>7. Περιορισμός Ευθύνης</h2>
                            <p>
                                Δεν ευθυνόμαστε για έμμεσες, τυχαίες ή επακόλουθες ζημίες
                                που προκύπτουν από τη χρήση των προϊόντων ή υπηρεσιών μας.
                            </p>

                            <h2>8. Επικοινωνία</h2>
                            <p>
                                Για ερωτήσεις σχετικά με αυτούς τους όρους, επικοινωνήστε μαζί μας μέσω της
                                <Link to="/contact" className="text-primary hover:underline ml-1">σελίδας επικοινωνίας</Link>.
                            </p>
                        </div>
                    )}
                </motion.div>

                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <Link to="/privacy" className="hover:text-primary">Πολιτική Απορρήτου</Link>
                    <span className="mx-3">•</span>
                    <Link to="/" className="hover:text-primary">Αρχική</Link>
                </div>
            </main>
        </div>
    );
}
